import React from 'react';

import { Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useProviderDocumentsQuery } from '@uplift/queries';
import { Button, Loader, NoItemsOverlayBody, ProviderDialogNavigation, ProviderLayout, ReviewLayout } from '@uplift/ui-v2';
import { UploadProviderDocumentModal } from './UploadProviderDocumentModal';
import { makeStyles } from 'tss-react/mui';

import { DocumentsList } from './DocumentsList';
import { DocumentPreview } from './DocumentPreview';
import { DocumentLayout } from './DocumentPreview/components';

const useStyles = makeStyles()(() => ({
  emptyState: {
    maxWidth: 480,
    margin: '0 auto',
  },
}));

export const DocumentsCenterPageContent: React.VFC = () => {
  const { classes } = useStyles();
  const l10n = useL10n();
  const me = useCurrentuser();
  const queryClient = useQueryClient();
  const { data: documents = [] } = useProviderDocumentsQuery(me.id);
  const [selectedDocumentId, setSelectedDocumentId] = React.useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const selectedDocument = documents.find(doc => doc.id === selectedDocumentId) ?? documents[0] ?? null;

  return (
    <>
      <ProviderDialogNavigation
        position="sticky"
        variant="outlined"
        leftActions={<Typography variant="h6" sx={{ fontSize: '18px', lineHeight: '18px' }}>Documents Center</Typography>}
        rightActions={(
          <Button variant="contained" color="secondary" onClick={() => setUploadOpen(true)}>
            Upload Document
          </Button>
        )}
      />
      {documents.length === 0 ? (
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <NoItemsOverlayBody
            className={classes.emptyState}
            icon={null}
            message="No documents yet"
            subtitle={l10n.documentCenter.emptyStateSubtitle}
            button={(
              <Button variant="contained" color="secondary" onClick={() => setUploadOpen(true)}>
                Upload Document
              </Button>
            )}
          />
        </Box>
      ) : (
        <ReviewLayout
          sidebarWidth="350px"
          minSidebarWidth="300px"
          sidebar={(
            <DocumentsList
              documents={documents}
              selectedDocumentId={selectedDocument?.id}
              onSelectDocument={(documentId) => {
                setSelectedDocumentId(documentId);
              }}
            />
          )}
          content={selectedDocument && (
            <React.Suspense fallback={(
              <DocumentLayout
                title=" "
                subtitle=" "
              >
                <Loader minHeight={300} color="secondary" />
              </DocumentLayout>
            )}
            >
              <DocumentPreview
                document={selectedDocument}
                setSelectedDocumentId={setSelectedDocumentId}
              />
            </React.Suspense>
          )}
        />
      )}
      <UploadProviderDocumentModal
        providerId={me.id}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={async () => {
          await queryClient.invalidateQueries({ queryKey: ['user', me.id, 'documents'] });
          setUploadOpen(false);
        }}
      />
    </>
  );
};

export const DocumentsCenterPage: React.FC = () => {
  return (
    <ProviderLayout>
      <DocumentsCenterPageContent />
    </ProviderLayout>
  );
};
